import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileSpreadsheet, Download, TrendingUp, BarChart3, DollarSign, Layers, Activity, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { SearchableSelect } from '@/components/SearchableSelect';


// Standard deviation scenarios
const SCENARIOS = [
  { name: 'Worst Case (-2σ)', factor: -2, color: 'bg-red-50 text-red-700 border-red-200' },
  { name: 'Below Average (-1σ)', factor: -1, color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { name: 'Base Case (Mean)', factor: 0, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { name: 'Above Average (+1σ)', factor: 1, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { name: 'Best Case (+2σ)', factor: 2, color: 'bg-green-50 text-green-700 border-green-200' }
];

export default function FeeTableAnalyticsStatic() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Default inputs
  const [inputs, setInputs] = useState({
    municipality: 'Sample Municipality',
    account_executive: 'Gina Anderburg',
    state: 'TX',
    annual_transactions: 121046,
    service_rate: 2.2,
    service_id: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    }
  };

  const handleServiceChange = async (serviceName) => {
    try {
      const response = await api.get('/admin/services');
      
      const service = response.data.services.find(s => s.name === serviceName);
      if (service) {
        setSelectedService(service);
        setInputs(prev => ({ ...prev, service_id: service.id }));
        await loadAnalytics(service.id);
      }
    } catch (error) {
      console.error('Error fetching service details:', error);
    }
  };

  const loadAnalytics = async (serviceId) => {
    if (!serviceId) return;
    
    try {
      setLoading(true);
      
      const response = await api.post(
        '/fee-table/calculate',
        {
          annual_transactions: inputs.annual_transactions,
          service_rate: inputs.service_rate / 100,
          service_id: serviceId
        }
      );
      
      console.log('Analytics API response:', response.data);
      
      // Debug: Log the first tier to see structure
      if (response.data.tier_breakdown && response.data.tier_breakdown.length > 0) {
        console.log('Sample tier data:', JSON.stringify(response.data.tier_breakdown[0], null, 2));
      }
      
      // Check if we have the expected data structure
      if (!response.data || !response.data.tier_breakdown) {
        console.error('Invalid response structure:', response.data);
        toast.error('Service does not have collection amounts configured');
        setAnalyticsData(null);
        return;
      }
      
      setAnalyticsData(response.data);
      toast.success('Analytics loaded successfully');
    } catch (error) {
      console.error('Error loading analytics:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to load analytics';
      toast.error(errorMsg);
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  };

  const calculateScenarioData = (scenario) => {
    if (!analyticsData || !analyticsData.tier_breakdown) return null;

    const stdDev = inputs.annual_transactions * 0.15; // 15% std deviation
    const adjustmentFactor = 1 + (scenario.factor * 0.15); // Adjust by ±15% per σ
    
    let totals = {
      transactions: 0,
      collections: 0,
      gross_revenue: 0,
      total_cost: 0,
      net_revenue: 0
    };

    const tiers = analyticsData.tier_breakdown.map(tier => {
      const adjustedTransactions = tier.transactions * adjustmentFactor;
      const adjustedCollections = tier.collections * adjustmentFactor;
      const adjustedGrossRevenue = tier.gross_revenue * adjustmentFactor;
      const adjustedCost = tier.total_cost * adjustmentFactor;
      const adjustedNetRevenue = tier.net_revenue * adjustmentFactor;

      totals.transactions += adjustedTransactions;
      totals.collections += adjustedCollections;
      totals.gross_revenue += adjustedGrossRevenue;
      totals.total_cost += adjustedCost;
      totals.net_revenue += adjustedNetRevenue;

      return {
        ...tier,
        adjusted_transactions: adjustedTransactions,
        adjusted_collections: adjustedCollections,
        adjusted_gross_revenue: adjustedGrossRevenue,
        adjusted_cost: adjustedCost,
        adjusted_net_revenue: adjustedNetRevenue,
        gross_take_rate: (adjustedGrossRevenue / adjustedCollections * 100) || 0,
        net_revenue_per_txn: adjustedNetRevenue / adjustedTransactions || 0,
        net_margin: (adjustedNetRevenue / adjustedGrossRevenue * 100) || 0
      };
    });

    totals.gross_take_rate = (totals.gross_revenue / totals.collections * 100) || 0;
    totals.net_revenue_per_txn = totals.net_revenue / totals.transactions || 0;
    totals.net_margin = (totals.net_revenue / totals.gross_revenue * 100) || 0;

    return { tiers, totals };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  if (!selectedService) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/fee-table-calculator')}
            className="mb-6 flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Calculator
          </button>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
            <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Select a Service</h2>
            <p className="text-slate-600 mb-6">Choose a service to view comprehensive analytics</p>
            
            <div className="max-w-md mx-auto">
              <SearchableSelect
                options={services}
                value=""
                onChange={handleServiceChange}
                placeholder="Select service..."
                displayKey="name"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData || !analyticsData.tier_breakdown) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/fee-table-calculator')}
            className="mb-6 flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-12 text-center">
            <p className="text-slate-600">No analytics data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/fee-table-calculator')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Calculator
          </button>
          <button className="btn-primary text-sm">
            <Download className="w-4 h-4 mr-2" />
            Export to Excel
          </button>
        </div>

        {/* Title Card */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-8 mb-6 text-white">
          <h1 className="text-3xl font-bold mb-2">Fee Table Analytics - Full View</h1>
          <p className="text-indigo-100 mb-4">{selectedService.name} - Revenue Projection Model</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <p className="text-indigo-100 text-sm mb-1">Municipality</p>
              <p className="font-bold">{inputs.municipality}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <p className="text-indigo-100 text-sm mb-1">Account Executive</p>
              <p className="font-bold">{inputs.account_executive}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <p className="text-indigo-100 text-sm mb-1">State</p>
              <p className="font-bold">{inputs.state}</p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <p className="text-indigo-100 text-sm mb-1">Annual Transactions</p>
              <p className="font-bold">{formatNumber(inputs.annual_transactions)}</p>
            </div>
          </div>
        </div>

        {/* Base Case Summary */}
        {analyticsData.summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <p className="text-xs text-slate-600 mb-1">Total Collections</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(analyticsData.summary.total_collections)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <p className="text-xs text-slate-600 mb-1">Gross Revenue</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(analyticsData.summary.total_revenue)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <p className="text-xs text-slate-600 mb-1">Gross Take Rate</p>
              <p className="text-2xl font-bold text-indigo-600">{formatPercent(analyticsData.summary.gross_take_rate)}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <p className="text-xs text-slate-600 mb-1">Avg Fee/Transaction</p>
              <p className="text-2xl font-bold text-emerald-600">{formatCurrency(analyticsData.summary.avg_payment_fee)}</p>
            </div>
          </div>
        )}

        {/* Important Note about Negative Values */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-1">Understanding Negative Values</h3>
              <p className="text-sm text-amber-800">
                Low-value transactions (typically under $50) often show <strong>negative net revenue</strong> because merchant processing costs 
                exceed the service fee collected. This is expected and normal for payment processing. Higher-value transactions are profitable 
                and offset these losses in the overall revenue model.
              </p>
            </div>
          </div>
        </div>

        {/* SECTION 1: Base Case Revenue Analytics */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
            <h2 className="text-xl font-bold text-slate-900 uppercase tracking-wide">Base Case Revenue Analytics</h2>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
          </div>

        {/* Revenue by Collection Tier Table */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-6 overflow-hidden">
          <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-600" />
              Revenue Breakdown by Payment Method & Tier
            </h3>
            <p className="text-xs text-slate-600 mt-1">Shows revenue distribution across ACH, Credit Card, AMEX, and Debit for each collection tier</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Tier Range</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Transactions</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Collections</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">ACH Rev</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">CC Rev</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">AMEX Rev</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">DEBIT Rev</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Total Rev</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {analyticsData.tier_breakdown.map((tier, idx) => {
                  const paymentBreakdown = tier.payment_breakdown || {};
                  
                  // Backend returns UPPERCASE keys: ACH, CC, AMEX, DEBIT
                  const achRev = paymentBreakdown.ACH?.gross_revenue || 0;
                  const ccRev = paymentBreakdown.CC?.gross_revenue || 0;
                  const amexRev = paymentBreakdown.AMEX?.gross_revenue || 0;
                  const debitRev = paymentBreakdown.DEBIT?.gross_revenue || 0;
                  
                  return (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{tier.range}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{formatNumber(tier.transactions)}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(tier.collections)}</td>
                      <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(achRev)}</td>
                      <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(ccRev)}</td>
                      <td className="px-4 py-3 text-right text-purple-600">{formatCurrency(amexRev)}</td>
                      <td className="px-4 py-3 text-right text-orange-600">{formatCurrency(debitRev)}</td>
                      <td className="px-4 py-3 text-right text-indigo-600 font-bold">{formatCurrency(tier.gross_revenue)}</td>
                    </tr>
                  );
                })}
                {analyticsData.summary && (
                  <tr className="bg-slate-100 font-bold">
                    <td className="px-4 py-3">TOTAL</td>
                    <td className="px-4 py-3 text-right">{formatNumber(analyticsData.summary.total_transactions)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(analyticsData.summary.total_collections)}</td>
                    <td className="px-4 py-3 text-right text-blue-600">
                      {formatCurrency(analyticsData.payment_method_totals?.ACH?.revenue || 0)}
                    </td>
                    <td className="px-4 py-3 text-right text-emerald-600">
                      {formatCurrency(analyticsData.payment_method_totals?.CC?.revenue || 0)}
                    </td>
                    <td className="px-4 py-3 text-right text-purple-600">
                      {formatCurrency(analyticsData.payment_method_totals?.AMEX?.revenue || 0)}
                    </td>
                    <td className="px-4 py-3 text-right text-orange-600">
                      {formatCurrency(analyticsData.payment_method_totals?.DEBIT?.revenue || 0)}
                    </td>
                    <td className="px-4 py-3 text-right text-indigo-600">{formatCurrency(analyticsData.summary.total_revenue)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div> {/* End Base Case Section */}

        {/* SECTION 2: Standard Deviation Scenario Analysis */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-emerald-300 to-transparent"></div>
            <h2 className="text-xl font-bold text-slate-900 uppercase tracking-wide">Sensitivity Analysis: Transaction Volume Scenarios</h2>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-emerald-300 to-transparent"></div>
          </div>

        {/* Scenario Analysis Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-6 p-6">
          <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Five Scenario Revenue Projections
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            Revenue projections across five scenarios based on transaction volume variability (±2 standard deviations at 15%). 
            This sensitivity analysis helps understand revenue performance under different transaction volume conditions.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {SCENARIOS.map((scenario) => (
              <div key={scenario.name} className={`rounded-lg border p-3 ${scenario.color}`}>
                <p className="text-xs font-semibold mb-1">{scenario.name}</p>
                <p className="text-xs opacity-75">σ factor: {scenario.factor > 0 ? '+' : ''}{scenario.factor}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Scenario Tables */}
        {SCENARIOS.map((scenario) => {
          const scenarioData = calculateScenarioData(scenario);
          if (!scenarioData) return null;

          return (
            <div key={scenario.name} className="mb-8">
              {/* Scenario Header Card */}
              <div className={`rounded-lg border-2 ${scenario.color} p-4 mb-4`}>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  {scenario.name}
                </h3>
                <p className="text-xs opacity-75 mt-1">Standard deviation factor: {scenario.factor > 0 ? '+' : ''}{scenario.factor}σ | Transaction volume: {formatNumber(scenarioData.totals.transactions)}</p>
              </div>

              {/* Revenue & Collections Table */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-4 overflow-hidden">
                <div className={`px-6 py-3 border-b ${scenario.color}`}>
                  <h4 className="text-sm font-bold flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Revenue & Collections by Tier
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Tier</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Transactions</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Collections</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Gross Revenue</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Merchant Costs</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Net Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {scenarioData.tiers.map((tier, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">{tier.range}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{formatNumber(tier.adjusted_transactions)}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(tier.adjusted_collections)}</td>
                          <td className="px-4 py-3 text-right text-blue-600 font-semibold">{formatCurrency(tier.adjusted_gross_revenue)}</td>
                          <td className="px-4 py-3 text-right text-red-600">{formatCurrency(tier.adjusted_cost)}</td>
                          <td className="px-4 py-3 text-right text-emerald-600 font-bold">{formatCurrency(tier.adjusted_net_revenue)}</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-100 font-bold">
                        <td className="px-4 py-3">TOTAL</td>
                        <td className="px-4 py-3 text-right">{formatNumber(scenarioData.totals.transactions)}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(scenarioData.totals.collections)}</td>
                        <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(scenarioData.totals.gross_revenue)}</td>
                        <td className="px-4 py-3 text-right text-red-600">{formatCurrency(scenarioData.totals.total_cost)}</td>
                        <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(scenarioData.totals.net_revenue)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Gross Take Rate Per Transaction Table */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-4 overflow-hidden">
                <div className={`px-6 py-3 border-b ${scenario.color}`}>
                  <h4 className="text-sm font-bold flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Gross Take Rate Per Transaction
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Tier</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Collections/Txn</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Gross Revenue/Txn</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Gross Take Rate %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {scenarioData.tiers.map((tier, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">{tier.range}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(tier.midpoint)}</td>
                          <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(tier.adjusted_gross_revenue / tier.adjusted_transactions)}</td>
                          <td className="px-4 py-3 text-right text-indigo-600 font-bold">{formatPercent(tier.gross_take_rate)}</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-100 font-bold">
                        <td className="px-4 py-3">AVERAGE</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(scenarioData.totals.collections / scenarioData.totals.transactions)}</td>
                        <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(scenarioData.totals.gross_revenue / scenarioData.totals.transactions)}</td>
                        <td className="px-4 py-3 text-right text-indigo-600">{formatPercent(scenarioData.totals.gross_take_rate)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Net Revenue Per Transaction Table */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-4 overflow-hidden">
                <div className={`px-6 py-3 border-b ${scenario.color}`}>
                  <h4 className="text-sm font-bold flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Net Revenue Per Transaction
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Tier</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Gross Revenue/Txn</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Cost/Txn</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Net Revenue/Txn</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {scenarioData.tiers.map((tier, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">{tier.range}</td>
                          <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(tier.adjusted_gross_revenue / tier.adjusted_transactions)}</td>
                          <td className="px-4 py-3 text-right text-red-600">{formatCurrency(tier.adjusted_cost / tier.adjusted_transactions)}</td>
                          <td className="px-4 py-3 text-right text-emerald-600 font-bold">{formatCurrency(tier.net_revenue_per_txn)}</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-100 font-bold">
                        <td className="px-4 py-3">AVERAGE</td>
                        <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(scenarioData.totals.gross_revenue / scenarioData.totals.transactions)}</td>
                        <td className="px-4 py-3 text-right text-red-600">{formatCurrency(scenarioData.totals.total_cost / scenarioData.totals.transactions)}</td>
                        <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(scenarioData.totals.net_revenue_per_txn)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Net Revenue Margin Table */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 mb-4 overflow-hidden">
                <div className={`px-6 py-3 border-b ${scenario.color}`}>
                  <h4 className="text-sm font-bold flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Net Revenue Margin (GPM)
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Tier</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Gross Revenue</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Net Revenue</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Net Margin %</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">GPM %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {scenarioData.tiers.map((tier, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">{tier.range}</td>
                          <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(tier.adjusted_gross_revenue)}</td>
                          <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(tier.adjusted_net_revenue)}</td>
                          <td className="px-4 py-3 text-right text-indigo-600 font-bold">{formatPercent(tier.net_margin)}</td>
                          <td className="px-4 py-3 text-right text-purple-600 font-bold">{formatPercent(tier.gpm)}</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-100 font-bold">
                        <td className="px-4 py-3">TOTAL</td>
                        <td className="px-4 py-3 text-right text-blue-600">{formatCurrency(scenarioData.totals.gross_revenue)}</td>
                        <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(scenarioData.totals.net_revenue)}</td>
                        <td className="px-4 py-3 text-right text-indigo-600">{formatPercent(scenarioData.totals.net_margin)}</td>
                        <td className="px-4 py-3 text-right text-purple-600">{formatPercent(scenarioData.totals.net_margin)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })}

        {/* Summary Comparison Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-indigo-600" />
            Scenario Summary Comparison
          </h3>
          <p className="text-xs text-slate-600 mb-4">Side-by-side comparison of key metrics across all five scenarios with variance from base case</p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Scenario</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Transactions</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Gross Revenue</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Net Revenue</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Net Margin %</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Variance from Base</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {SCENARIOS.map((scenario) => {
                  const scenarioData = calculateScenarioData(scenario);
                  const baseData = calculateScenarioData(SCENARIOS[2]); // Base case
                  const variance = scenarioData && baseData 
                    ? ((scenarioData.totals.net_revenue - baseData.totals.net_revenue) / baseData.totals.net_revenue * 100) 
                    : 0;
                  
                  return (
                    <tr key={scenario.name} className={`hover:bg-slate-50 ${scenario.factor === 0 ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{scenario.name}</td>
                      <td className="px-4 py-3 text-right">{scenarioData && formatNumber(scenarioData.totals.transactions)}</td>
                      <td className="px-4 py-3 text-right text-blue-600">{scenarioData && formatCurrency(scenarioData.totals.gross_revenue)}</td>
                      <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{scenarioData && formatCurrency(scenarioData.totals.net_revenue)}</td>
                      <td className="px-4 py-3 text-right text-indigo-600 font-semibold">{scenarioData && formatPercent(scenarioData.totals.net_margin)}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${variance > 0 ? 'text-emerald-600' : variance < 0 ? 'text-red-600' : 'text-slate-600'}`}>
                        {scenario.factor !== 0 && scenarioData ? `${variance > 0 ? '+' : ''}${variance.toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </div> {/* End Sensitivity Analysis Section */}
      </div>
    </div>
  );
}
